import React, { useEffect, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { fetchConfiguration, applyTheme } from '../services/configService';
import './ControlPanel.css';

interface CommitteeListItem {
  id: number;
  name: string;
  displayOrder: number;
  isActive: boolean;
  memberCount: number;
}

interface PresentationState {
  currentCommitteeIndex: number;
  currentCommitteeId: number | null;
  isRunning: boolean;
  isStopped: boolean;
  displayMode: 'Scrolling' | 'Cloud';
  slideDurationMs: number;
  scrollSpeed: number;
  lastUpdated: string;
}

const ControlPanel: React.FC = () => {
  const [committees, setCommittees] = useState<CommitteeListItem[]>([]);
  const [state, setState] = useState<PresentationState | null>(null);
  const [connection, setConnection] = useState<signalR.HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load configuration and apply theme on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const appConfig = await fetchConfiguration();
        applyTheme(appConfig.theme);
        console.log('Configuration loaded and applied');
      } catch (error) {
        console.error('Error loading configuration:', error);
      }
    };

    loadConfig();
  }, []);

  // Load committees
  useEffect(() => {
    const loadCommittees = async () => {
      try {
        const response = await fetch('/api/committees?activeOnly=true');
        const data: CommitteeListItem[] = await response.json();
        setCommittees(data);
      } catch (error) {
        console.error('Error loading committees:', error);
      }
    };

    loadCommittees();
  }, []);

  // Setup SignalR connection
  useEffect(() => {
    const connectSignalR = async () => {
      const newConnection = new signalR.HubConnectionBuilder()
        .withUrl('/hubs/presentation', {
          skipNegotiation: false,
          transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling
        })
        .withAutomaticReconnect()
        .build();

      newConnection.on('StateChanged', (newState: PresentationState) => {
        setState(newState);
      });

      newConnection.on('SlideChanged', (newState: PresentationState) => {
        setState(newState);
      });

      newConnection.on('ModeChanged', (newState: PresentationState) => {
        setState(newState);
      });

      newConnection.on('TimingChanged', (newState: PresentationState) => {
        setState(newState);
      });

      try {
        await newConnection.start();
        setIsConnected(true);
        setConnection(newConnection);
        
        // Get initial state
        const response = await fetch('/api/presentation/state');
        const initialState = await response.json();
        setState(initialState);
        setIsLoading(false);
      } catch (error) {
        console.error('Error connecting to SignalR:', error);
        setIsLoading(false);
      }
    };

    connectSignalR();

    return () => {
      if (connection) {
        connection.stop();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStart = async () => {
    if (!connection) return;
    try {
      await connection.invoke('StartPresentation');
    } catch (error) {
      console.error('Error starting presentation:', error);
    }
  };

  const handlePause = async () => {
    if (!connection) return;
    try {
      await connection.invoke('PausePresentation');
    } catch (error) {
      console.error('Error pausing presentation:', error);
    }
  };

  const handleStop = async () => {
    if (!connection) return;
    try {
      await connection.invoke('StopPresentation');
    } catch (error) {
      console.error('Error stopping presentation:', error);
    }
  };

  const handleNext = async () => {
    if (!state || committees.length === 0) return;
    const newIndex = (state.currentCommitteeIndex + 1) % committees.length;
    const committee = committees[newIndex];
    
    if (!connection) return;
    try {
      await connection.invoke('NextSlide', newIndex, committee.id);
    } catch (error) {
      console.error('Error moving to next slide:', error);
    }
  };

  const handlePrevious = async () => {
    if (!state || committees.length === 0) return;
    const newIndex = (state.currentCommitteeIndex - 1 + committees.length) % committees.length;
    const committee = committees[newIndex];
    
    if (!connection) return;
    try {
      await connection.invoke('PreviousSlide', newIndex, committee.id);
    } catch (error) {
      console.error('Error moving to previous slide:', error);
    }
  };

  const handleJumpToCommittee = async (index: number) => {
    if (!connection || committees.length === 0) return;
    const committee = committees[index];
    
    try {
      await connection.invoke('JumpToCommittee', index, committee.id);
    } catch (error) {
      console.error('Error jumping to committee:', error);
    }
  };

  const handleDisplayModeChange = async (mode: 'scrolling' | 'cloud') => {
    if (!connection) return;
    try {
      await connection.invoke('SetDisplayMode', mode);
    } catch (error) {
      console.error('Error changing display mode:', error);
    }
  };

  const handleSlideDurationChange = async (durationSeconds: number) => {
    if (!connection) return;
    try {
      await connection.invoke('SetSlideDuration', durationSeconds * 1000);
    } catch (error) {
      console.error('Error changing slide duration:', error);
    }
  };

  const handleScrollSpeedChange = async (speed: number) => {
    if (!connection) return;
    try {
      await connection.invoke('SetScrollSpeed', speed);
    } catch (error) {
      console.error('Error changing scroll speed:', error);
    }
  };

  const currentCommittee = state && committees.length > 0 
    ? committees[state.currentCommitteeIndex] 
    : null;

  if (isLoading) {
    return (
      <div className="control-panel loading">
        <div className="loading-message">Loading control panel...</div>
      </div>
    );
  }

  return (
    <div className="control-panel">
      <header className="control-header">
        <h1>Operator Control Panel</h1>
        <div className="connection-status">
          <span className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}></span>
          <span>{isConnected ? 'Connected' : 'Disconnected'}</span>
        </div>
      </header>

      <div className="control-content">
        {/* Current Status */}
        <section className="status-section">
          <h2>Current Status</h2>
          <div className="status-info">
            <div className="status-item">
              <span className="label">State:</span>
              <span className={`value ${state?.isStopped ? 'stopped' : state?.isRunning ? 'running' : 'paused'}`}>
                {state?.isStopped ? 'Stopped' : state?.isRunning ? 'Running' : 'Paused'}
              </span>
            </div>
            <div className="status-item">
              <span className="label">Current Committee:</span>
              <span className="value">
                {currentCommittee ? currentCommittee.name : 'None'}
              </span>
            </div>
            <div className="status-item">
              <span className="label">Slide:</span>
              <span className="value">
                {state ? `${state.currentCommitteeIndex + 1} / ${committees.length}` : 'N/A'}
              </span>
            </div>
            <div className="status-item">
              <span className="label">Display Mode:</span>
              <span className="value">
                {state?.displayMode === 'Cloud' ? 'Name Cloud' : 'Scrolling List'}
              </span>
            </div>
          </div>
        </section>

        {/* Primary Controls */}
        <section className="controls-section">
          <h2>Playback Controls</h2>
          <div className="button-grid">
            <button 
              className="control-btn primary start-btn"
              onClick={handleStart}
              disabled={!isConnected || (state?.isRunning && !state?.isStopped)}
            >
              ▶ Start
            </button>
            <button 
              className="control-btn pause-btn"
              onClick={handlePause}
              disabled={!isConnected || !state?.isRunning || state?.isStopped}
            >
              ⏸ Pause
            </button>
            <button 
              className="control-btn stop-btn"
              onClick={handleStop}
              disabled={!isConnected || state?.isStopped}
            >
              ⏹ Stop
            </button>
            <button 
              className="control-btn"
              onClick={handlePrevious}
              disabled={!isConnected || committees.length === 0}
            >
              ◀ Previous
            </button>
            <button 
              className="control-btn"
              onClick={handleNext}
              disabled={!isConnected || committees.length === 0}
            >
              Next ▶
            </button>
          </div>
        </section>

        {/* Committee Selection */}
        <section className="committee-section">
          <h2>Jump to Committee</h2>
          <div className="committee-list">
            {committees.map((committee, index) => (
              <button
                key={committee.id}
                className={`committee-btn ${state?.currentCommitteeIndex === index ? 'active' : ''}`}
                onClick={() => handleJumpToCommittee(index)}
                disabled={!isConnected}
              >
                {committee.name}
              </button>
            ))}
          </div>
        </section>

        {/* Display Settings */}
        <section className="settings-section">
          <h2>Display Settings</h2>
          
          <div className="setting-group">
            <label>Display Mode:</label>
            <div className="button-group">
              <button
                className={`mode-btn ${state?.displayMode === 'Scrolling' ? 'active' : ''}`}
                onClick={() => handleDisplayModeChange('scrolling')}
                disabled={!isConnected}
              >
                📜 Scrolling List
              </button>
              <button
                className={`mode-btn ${state?.displayMode === 'Cloud' ? 'active' : ''}`}
                onClick={() => handleDisplayModeChange('cloud')}
                disabled={!isConnected}
              >
                ☁️ Name Cloud
              </button>
            </div>
          </div>

          <div className="setting-group">
            <label>Slide Duration (seconds):</label>
            <div className="slider-control">
              <input
                type="range"
                min="5"
                max="120"
                step="5"
                value={state ? Math.round(state.slideDurationMs / 1000) : 25}
                onChange={(e) => handleSlideDurationChange(Number(e.target.value))}
                disabled={!isConnected}
              />
              <span className="slider-value">
                {state ? Math.round(state.slideDurationMs / 1000) : 25}s
              </span>
            </div>
          </div>

          <div className="setting-group">
            <label>Scroll Speed (pixels/sec):</label>
            <div className="slider-control">
              <input
                type="range"
                min="10"
                max="100"
                step="5"
                value={state?.scrollSpeed ?? 30}
                onChange={(e) => handleScrollSpeedChange(Number(e.target.value))}
                disabled={!isConnected}
              />
              <span className="slider-value">
                {state?.scrollSpeed ?? 30}px/s
              </span>
            </div>
          </div>
        </section>

        {/* Quick Actions */}
        <section className="actions-section">
          <h2>Quick Actions</h2>
          <div className="action-buttons">
            <a href="/present" target="_blank" rel="noopener noreferrer" className="action-link">
              🖥️ Open Presentation Display
            </a>
            <a href="/" className="action-link secondary">
              🏠 Back to Home
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ControlPanel;
